import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCustomers } from '../context/CustomerContext';
import CustomerCard from '../components/CustomerCard';
import CustomerModal from '../components/CustomerModal';
import MessageModal from '../components/MessageModal';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPlus, FiSearch, FiGrid, FiList, FiFilter, FiLoader } = FiIcons;

function Customers() {
  const { state, dispatch } = useCustomers();
  const { customers, tags, searchTerm, loading, modalLoading, error } = state;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [selectedTag, setSelectedTag] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  const [messageRecipient, setMessageRecipient] = useState(null);

  const filteredCustomers = customers.filter((customer) => {
    const matchesSearch =
      customer.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.phone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.interestedVehicle?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTag = selectedTag === 'all' || customer.tags?.includes(selectedTag);
    const matchesStatus = statusFilter === 'all' || customer.status === statusFilter;
    return matchesSearch && matchesTag && matchesStatus;
  });

  const handleAddCustomer = () => {
    setEditingCustomer(null);
    setIsModalOpen(true);
  };

  const handleEditCustomer = (customer) => {
    setEditingCustomer(customer);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCustomer(null);
  };

  const handleSendMessage = (customer) => {
    setMessageRecipient(customer);
    setIsMessageModalOpen(true);
  };

  if (error) {
    return (
      <div className="text-center py-8 sm:py-12">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 sm:p-6 max-w-md mx-auto">
          <h3 className="text-base sm:text-lg font-medium text-red-900 mb-2">Error Loading Customers</h3>
          <p className="text-red-700 text-sm sm:text-base">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 bg-red-600 text-white px-4 py-2 rounded font-medium hover:bg-red-700 text-sm"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header Actions */}
      <div className="flex flex-col space-y-3 sm:space-y-4">
        {/* Search and Add Button Row */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <div className="flex items-center space-x-2 sm:space-x-4 flex-1">
            <div className="relative flex-1 sm:flex-none">
              <SafeIcon
                icon={FiSearch}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4"
              />
              <input
                type="text"
                placeholder="Search customers..."
                value={searchTerm}
                onChange={(e) => dispatch({ type: 'SET_SEARCH', payload: e.target.value })}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent w-full sm:w-64"
              />
            </div>
            {/* Mobile filter toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors sm:hidden"
            >
              <SafeIcon icon={FiFilter} className="w-5 h-5" />
            </button>
          </div>
          <div className="flex items-center justify-between sm:justify-end space-x-3">
            {/* View Mode Toggle */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'grid' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'
                }`}
              >
                <SafeIcon icon={FiGrid} className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'list' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'
                }`}
              >
                <SafeIcon icon={FiList} className="w-4 h-4" />
              </button>
            </div>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleAddCustomer}
              className="bg-primary-600 text-white px-3 sm:px-4 py-2 rounded-lg font-medium hover:bg-primary-700 transition-colors flex items-center space-x-2 text-sm sm:text-base"
              disabled={modalLoading}
            >
              {modalLoading ? (
                <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
              ) : (
                <SafeIcon icon={FiPlus} className="w-4 h-4" />
              )}
              <span className="hidden sm:inline">Add Customer</span>
              <span className="sm:hidden">Add</span>
            </motion.button>
          </div>
        </div>
        {/* Filters Row - Desktop always visible, Mobile collapsible */}
        <div
          className={`flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 ${
            showFilters ? 'block' : 'hidden sm:flex'
          }`}
        >
          <select
            value={selectedTag}
            onChange={(e) => setSelectedTag(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
          >
            <option value="all">All Tags</option>
            {tags.map((tag) => (
              <option key={tag} value={tag}>
                {tag}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="prospect">Prospect</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>
      {/* Stats */}
      <div className="bg-white p-3 sm:p-4 rounded-lg border border-gray-200">
        <div className="flex items-center justify-between text-xs sm:text-sm text-gray-600">
          <span>{filteredCustomers.length} customers shown</span>
          <span>{customers.length} total customers</span>
        </div>
      </div>
      {/* Loading State */}
      {loading && customers.length === 0 && (
        <div className="text-center py-8 sm:py-12">
          <SafeIcon
            icon={FiLoader}
            className="w-6 h-6 sm:w-8 sm:h-8 text-primary-600 mx-auto mb-4 animate-spin"
          />
          <p className="text-gray-500 text-sm sm:text-base">Loading customers...</p>
        </div>
      )}
      {/* Customers Grid/List */}
      <AnimatePresence mode="wait">
        {!loading && filteredCustomers.length > 0 ? (
          <motion.div
            key={viewMode}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className={
              viewMode === 'grid'
                ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6'
                : 'space-y-3 sm:space-y-4'
            }
          >
            {filteredCustomers.map((customer, index) => (
              <motion.div
                key={customer.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <CustomerCard
                  customer={customer}
                  viewMode={viewMode}
                  onEdit={() => handleEditCustomer(customer)}
                  onSendMessage={handleSendMessage}
                />
              </motion.div>
            ))}
          </motion.div>
        ) : !loading ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-8 sm:py-12"
          >
            <SafeIcon
              icon={FiSearch}
              className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 mx-auto mb-4"
            />
            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">
              {searchTerm || selectedTag !== 'all' || statusFilter !== 'all'
                ? 'No customers found'
                : 'No customers yet'}
            </h3>
            <p className="text-gray-500 mb-6 text-sm sm:text-base px-4">
              {searchTerm || selectedTag !== 'all' || statusFilter !== 'all'
                ? 'Try adjusting your search or filter criteria'
                : 'Start building your customer base by adding your first customer'}
            </p>
            {!searchTerm && selectedTag === 'all' && statusFilter === 'all' && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleAddCustomer}
                className="bg-primary-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-medium hover:bg-primary-700 transition-colors text-sm sm:text-base"
              >
                Add Your First Customer
              </motion.button>
            )}
          </motion.div>
        ) : null}
      </AnimatePresence>
      {/* Customer Modal */}
      <CustomerModal isOpen={isModalOpen} onClose={handleCloseModal} customer={editingCustomer} />
      
      {/* Message Modal */}
      <MessageModal
        isOpen={isMessageModalOpen}
        onClose={() => {
          setIsMessageModalOpen(false);
          setMessageRecipient(null);
        }}
        recipient={messageRecipient}
      />
    </div>
  );
}

export default Customers;